/** Lowercase, trim, and collapse commas/whitespace so partial address search is reliable. */
export const normalizeForSearch = (v) => {
  if (v == null || v === '') return '';
  return String(v)
    .toLowerCase()
    .replace(/[\s\u00a0,;]+/g, ' ')
    .trim();
};

export const searchTokensFromInput = (searchInput) => {
  const n = normalizeForSearch(searchInput);
  if (!n) return [];
  return n.split(/\s+/).filter(Boolean);
};

const formatDateTimeForSearch = (isoString, timezone = 'America/Los_Angeles') => {
  if (!isoString) return [];
  const raw = String(isoString);
  const formats = [raw];

  try {
    const date = new Date(raw);
    if (!Number.isNaN(date.getTime())) {
      const datePart = date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        timeZone: timezone,
      });
      const time24 = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: timezone,
      });
      const time12 = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: timezone,
      });

      formats.push(`${datePart} ${time24}`, `${datePart} ${time12}`, time24, time12);
    }
  } catch {
    // Keep the raw value searchable if Date parsing fails.
  }

  return formats;
};

/** All tokens must appear in haystack (any order) — e.g. "Barstow" matches "655 S 7th Ave Barstow CA 92311". */
export const textIncludesAllSearchTokens = (normalizedHaystack, tokens) => {
  if (tokens.length === 0) return true;
  return tokens.every((t) => normalizedHaystack.includes(t));
};

/**
 * LOS + space type titles as on trip cards (labels only—no load/unload times).
 * Uses `load_unload_times` map by `space_type` / `space_type_id` when inline titles are missing.
 */
export const resolveLosSpaceTitlesForSearch = (trip, spaceTypeMap = {}) => {
  if (trip.losTitle && trip.spaceTypeTitle) {
    return { los: String(trip.losTitle), space: String(trip.spaceTypeTitle) };
  }
  if (trip.los_title && trip.space_type_title) {
    return { los: String(trip.los_title), space: String(trip.space_type_title) };
  }
  const key = trip.space_type ?? trip.space_type_id;
  const stData =
    key != null ? (spaceTypeMap[key] ?? spaceTypeMap[String(key)]) : undefined;
  if (typeof stData === 'string') {
    return { los: stData, space: stData };
  }
  if (stData && typeof stData === 'object') {
    const los = stData.los_title || stData.losTitle || stData.title || '';
    const space =
      stData.st_title ||
      stData.space_type_title ||
      stData.original_title ||
      stData.title ||
      '';
    return { los: String(los || ''), space: String(space || los || '') };
  }
  const fallback = trip.space_type != null ? String(trip.space_type) : '';
  return { los: fallback, space: fallback };
};

export const getTripSearchHaystack = (trip, spaceTypeMap = {}, timezone = 'America/Los_Angeles') => {
  const { los, space } = resolveLosSpaceTitlesForSearch(trip, spaceTypeMap);
  const timeFields = [
    trip.pickup_time_resolved,
    trip.pickup_time,
    trip.schedule_time,
    trip.scheduleTime,
    trip.dropoff_time_resolved,
    trip.dropoff_time,
    trip.appointment_time,
    trip.appointmentTime,
    trip.trip_details?.approximate_dropoff_time,
  ].flatMap((time) => formatDateTimeForSearch(time, timezone));

  return normalizeForSearch(
    [
      trip.client_name,
      trip.trip_id,
      trip.id,
      trip.pickup_address,
      trip.dropoff_address,
      trip.jobOriginAddress,
      trip.jobDestinationAddress,
      trip.los_title,
      trip.space_type_title,
      trip.losTitle,
      trip.spaceTypeTitle,
      los,
      space,
      ...timeFields,
    ]
      .map((p) => (p == null || p === '' ? '' : String(p)))
      .join(' ')
  );
};

/** Client, ids, addresses, pickup/dropoff times, LOS/space type (titles on card). */
export const tripMatchesTripsPageSearch = (trip, searchInput, spaceTypeMap = {}, timezone = 'America/Los_Angeles') => {
  const tokens = searchTokensFromInput(searchInput);
  if (tokens.length === 0) return true;
  return textIncludesAllSearchTokens(getTripSearchHaystack(trip, spaceTypeMap, timezone), tokens);
};

export const driverNameMatchesSearch = (driverName, searchInput) => {
  const tokens = searchTokensFromInput(searchInput);
  if (tokens.length === 0) return true;
  return textIncludesAllSearchTokens(normalizeForSearch(driverName || ''), tokens);
};
